import { Component, ElementRef, computed, input, output, signal, viewChild } from "@angular/core";
import { SafeUrl } from "@angular/platform-browser";
import { MessageModule } from "primeng/message";
import { TabsModule } from "primeng/tabs";
import { CONSTANTS } from "../../../constants";

/** A page of thumbnails, labelled with the 1-based range it covers. */
export interface PhotoCapturePage {
  index: number;
  label: string;
}

/** A single thumbnail paired with its index in the full (unpaged) image list. */
export interface PhotoCaptureThumbnail {
  index: number;
  src: string | SafeUrl;
}

@Component({
  selector: "app-photo-capture",
  standalone: true,
  imports: [MessageModule, TabsModule],
  templateUrl: "./photo-capture.component.html",
  styleUrl: "./photo-capture.component.scss"
})
export class PhotoCaptureComponent {
  /** Thumbnail sources to render in the grid. */
  images = input<(string | SafeUrl)[]>([]);
  /** Disables the capture buttons. */
  disabled = input<boolean>(false);
  /** Hides the camera and library capture buttons. */
  hideCaptureButtons = input<boolean>(false);
  /** Force invalid styling when used without `app-form-field`. */
  invalid = input<boolean>(false);
  /** Show the "Adding N images..." message. */
  loading = input<boolean>(false);
  /** Show the "Optimizing N images..." message. */
  optimizing = input<boolean>(false);
  /** Count shown in the loading/optimizing messages. */
  processingCount = input<number>(0);
  /** Thumbnails rendered per page. */
  pageSize = input<number>(CONSTANTS.CONSTRAINTS.PHOTOS_PER_PAGE);
  /** Maximum number of photos that can be attached. */
  maxImages = input<number>(CONSTANTS.CONSTRAINTS.MAX_PHOTOS);
  cameraLabel = input<string>("Camera");
  libraryLabel = input<string>("Library");
  /** Base id used to keep the hidden file inputs unique per instance. */
  inputId = input<string>("photo-capture");
  testId = input<string>("app-photo-capture");

  /** Emitted with the raw files selected from camera or library. */
  readonly filesSelected = output<File[]>();
  /** Emitted with the index of the thumbnail the user tapped. */
  readonly imageClicked = output<number>();

  private readonly requestedPage = signal(0);

  /**
   * Trailing thumbnails the parent has already appended but is still optimizing.
   * They are withheld from the grid so a skeleton can stand in their place —
   * otherwise the full-size original paints and is then hot-swapped for the
   * compressed version, which is what we are trying to avoid.
   */
  private readonly pendingCount = computed(() =>
    (this.optimizing() ? Math.min(this.processingCount(), this.images().length) : 0)
  );

  /** Thumbnails that are safe to paint. */
  private readonly renderedCount = computed(() => this.images().length - this.pendingCount());

  /**
   * One placeholder per in-flight photo. While `loading` the parent has not appended
   * anything yet, so these sit after the existing thumbnails; while `optimizing` they
   * occupy the slots withheld by `pendingCount`.
   */
  private readonly skeletonCount = computed(() =>
    (this.loading() || this.optimizing() ? this.processingCount() : 0)
  );

  /** Total grid cells in play: paintable thumbnails plus placeholders. */
  private readonly cellCount = computed(() => this.renderedCount() + this.skeletonCount());

  protected readonly pageCount = computed(() =>
    Math.max(1, Math.ceil(this.cellCount() / this.pageSize()))
  );

  protected readonly activePage = computed(() =>
    Math.min(this.requestedPage(), this.pageCount() - 1)
  );

  /** Only page once there is more than a single page worth of cells. */
  protected readonly showPager = computed(() => this.cellCount() > this.pageSize());

  protected readonly pages = computed<PhotoCapturePage[]>(() => {
    const total = this.cellCount();
    const size = this.pageSize();
    return Array.from({ length: this.pageCount() }, (_, index) => {
      const first = index * size + 1;
      const last = Math.min((index + 1) * size, total);
      return { index, label: first === last ? `${first}` : `${first}-${last}` };
    });
  });

  protected readonly pagedImages = computed<PhotoCaptureThumbnail[]>(() => {
    const start = this.activePage() * this.pageSize();
    const end = Math.min(start + this.pageSize(), this.renderedCount());
    if (end <= start) {
      return [];
    }
    return this.images()
      .slice(start, end)
      .map((src, offset) => ({ index: start + offset, src }));
  });

  protected readonly skeletons = computed<number[]>(() => {
    const start = Math.max(this.activePage() * this.pageSize(), this.renderedCount());
    const end = Math.min((this.activePage() + 1) * this.pageSize(), this.cellCount());
    return Array.from({ length: Math.max(0, end - start) }, (_, index) => index);
  });

  /** Slots left before `maxImages` is reached; counts in-flight photos too. */
  protected readonly remainingSlots = computed(() =>
    Math.max(0, this.maxImages() - this.cellCount())
  );

  protected readonly atMaxImages = computed(() => this.remainingSlots() === 0);

  private readonly cameraInput = viewChild<ElementRef<HTMLInputElement>>("cameraInput");
  private readonly galleryInput = viewChild<ElementRef<HTMLInputElement>>("galleryInput");

  protected onPageChange(page: string | number): void {
    this.requestedPage.set(Number(page));
  }

  protected triggerCamera(): void {
    this.cameraInput()?.nativeElement.click();
  }

  protected triggerGallery(): void {
    this.galleryInput()?.nativeElement.click();
  }

  protected handleFiles(event: Event): void {
    const target = event.target as HTMLInputElement;
    const files = target.files ? Array.from(target.files) : [];
    target.value = "";

    if (files.length === 0) {
      return;
    }

    const remaining = this.remainingSlots();
    if (remaining === 0) {
      return;
    }

    const accepted = files.slice(0, remaining);

    // Jump to the page the new photos land on. The value is clamped by
    // `activePage` until the parent pushes the updated `images` back in.
    this.requestedPage.set(Math.floor((this.images().length + accepted.length - 1) / this.pageSize()));
    this.filesSelected.emit(accepted);
  }
}
